import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { Button, Row, Col, Table, Modal, ModalHeader, ModalBody, ModalFooter, Input, Label } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import { v4 as uuidv4 } from "uuid";
import Header from "../../../layout/Header/Header";
import { api, mensajeError, hoy, haceDias, dinero, numero, nombreUsuario, primero, Estado, pdfRequisicion } from "./gastosFlujoUtil";

// Paso 1 del flujo de gastos: Requisición -> (autoriza) -> se hace la Orden.
const conToken = () => ({ headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` } });
const nuevoRenglon = () => ({ id: uuidv4(), insumos: "", cantidad: "", observaciones: "" });

function RequisicionesGastos() {
  const { user } = useContext(AuthContext);
  const [inicio, setInicio] = useState(haceDias(90));
  const [fin, setFin] = useState(hoy());
  const [filtroStatus, setFiltroStatus] = useState("");
  const [requisiciones, setRequisiciones] = useState([]);
  const [cat, setCat] = useState({ insumos: [], solicitantes: [], empresas: [], subCategorias: [], proveedores: [] });
  const [nueva, setNueva] = useState(null);
  const [detalle, setDetalle] = useState(null); // { requisicion, renglones }
  const [orden, setOrden] = useState(null); // { requisicion, renglones:[{...insumo, precio}], proveedor, impuestos, fecha, observaciones }
  const [guardando, setGuardando] = useState(false);

  const cargar = () =>
    api.get(`requisicionesGastosFechas/${inicio}/${fin}`).then(setRequisiciones).catch((e) => Swal.fire({ icon: "error", title: "No se pudieron cargar", text: mensajeError(e) }));

  useEffect(() => {
    cargar();
  }, [inicio, fin]); // eslint-disable-line

  useEffect(() => {
    const pide = (url) => axios.get(url, conToken()).then((r) => r.data || []).catch(() => []);
    Promise.all([
      api.get("insumosGastos").catch(() => []),
      pide(process.env.REACT_APP_URL_SOLICITANTES),
      pide(process.env.REACT_APP_URL_EMPRESAS),
      pide(process.env.REACT_APP_URL_SUB_CATEGORIAS),
      api.get("proveedoresGastos").catch(() => []),
    ]).then(([insumos, solicitantes, empresas, subCategorias, proveedores]) =>
      setCat({
        insumos: insumos.filter((i) => i.is_active !== "No"),
        solicitantes: solicitantes.filter((s) => s.is_active !== "No"),
        empresas: empresas.filter((e) => e.is_active !== "No"),
        subCategorias: subCategorias.filter((s) => s.is_active !== "No" && s.categorias && s.categorias[0]),
        proveedores: proveedores.filter((p) => p.is_active !== "No"),
      })
    );
  }, []);

  const lista = useMemo(() => requisiciones.filter((r) => !filtroStatus || r.status === filtroStatus), [requisiciones, filtroStatus]);

  // ── Nueva requisición ──
  function abrirNueva() {
    setNueva({
      fecha: hoy(),
      solicitantes: "",
      empresas: cat.empresas.length === 1 ? cat.empresas[0]._id : "",
      subCategorias: "",
      observaciones: "",
      renglones: [nuevoRenglon()],
    });
  }
  const setRenglon = (id, campo, valor) =>
    setNueva((n) => ({ ...n, renglones: n.renglones.map((r) => (r.id === id ? { ...r, [campo]: valor } : r)) }));

  async function guardarNueva() {
    const renglones = nueva.renglones.filter((r) => r.insumos && Number(r.cantidad) > 0);
    if (!nueva.solicitantes) return Swal.fire({ icon: "warning", title: "Falta quién solicita" });
    if (!renglones.length) return Swal.fire({ icon: "warning", title: "Agrega al menos un insumo con cantidad" });
    const sub = cat.subCategorias.find((s) => s._id === nueva.subCategorias);
    setGuardando(true);
    try {
      await api.post("requisicionesGastos", {
        fecha: nueva.fecha,
        solicitantes: nueva.solicitantes,
        empresas: nueva.empresas || undefined,
        categorias: sub ? sub.categorias[0]._id : undefined,
        subCategorias: sub ? sub._id : undefined,
        observaciones: nueva.observaciones,
        renglones: renglones.map((r) => ({ insumos: r.insumos, cantidad: Number(r.cantidad), observaciones: r.observaciones })),
      });
      setNueva(null);
      await cargar();
      Swal.fire({ icon: "success", title: "Requisición creada", text: "Queda por autorizar.", timer: 1500, showConfirmButton: false });
    } catch (e) {
      Swal.fire({ icon: "error", title: "No se guardó", text: mensajeError(e) });
    }
    setGuardando(false);
  }

  // ── Acciones ──
  async function verDetalle(r, accion) {
    try {
      const d = await api.get(`requisicionesGastos/${r._id}`);
      if (accion === "pdf") return pdfRequisicion(d);
      if (accion === "orden") {
        return setOrden({
          requisicion: d.requisicion,
          renglones: d.renglones.map((x) => ({ ...x, precio: x.insumos && x.insumos[0] ? x.insumos[0].costo || 0 : 0 })),
          proveedor: "",
          impuestos: 0,
          fecha: hoy(),
          observaciones: "",
        });
      }
      setDetalle(d);
    } catch (e) {
      Swal.fire({ icon: "error", title: "No se pudo cargar", text: mensajeError(e) });
    }
  }

  async function autorizar(r, decision) {
    let motivo;
    if (decision === "Rechazada") {
      const res = await Swal.fire({ title: `Rechazar requisición ${r.idRequisicionGastos}`, input: "text", inputPlaceholder: "Motivo", showCancelButton: true, confirmButtonText: "Rechazar", cancelButtonText: "Cancelar" });
      if (res.dismiss || res.value === undefined) return;
      motivo = res.value;
    } else {
      const res = await Swal.fire({ icon: "question", title: `¿Autorizar la requisición ${r.idRequisicionGastos}?`, showCancelButton: true, confirmButtonText: "Autorizar", cancelButtonText: "Cancelar" });
      if (!res.isConfirmed && !res.value) return;
    }
    try {
      await api.patch(`requisicionesGastosAutorizar/${r._id}`, { decision, motivo });
      await cargar();
    } catch (e) {
      Swal.fire({ icon: "error", title: "No se pudo", text: mensajeError(e) });
    }
  }

  async function cancelar(r) {
    const res = await Swal.fire({ icon: "warning", title: `¿Cancelar la requisición ${r.idRequisicionGastos}?`, showCancelButton: true, confirmButtonText: "Sí, cancelar", cancelButtonText: "No" });
    if (!res.isConfirmed && !res.value) return;
    try {
      await api.patch(`requisicionesGastosCancelar/${r._id}`);
      await cargar();
    } catch (e) {
      Swal.fire({ icon: "error", title: "No se pudo cancelar", text: mensajeError(e) });
    }
  }

  // ── Hacer la orden de gasto ──
  const totalesOrden = useMemo(() => {
    if (!orden) return { subtotal: 0, iva: 0, total: 0 };
    const subtotal = orden.renglones.reduce((s, r) => s + Number(r.cantidad) * (Number(r.precio) || 0), 0);
    const iva = subtotal * (Number(orden.impuestos) / 100);
    return { subtotal, iva, total: subtotal + iva };
  }, [orden]);

  async function guardarOrden() {
    if (!orden.proveedor) return Swal.fire({ icon: "warning", title: "Elige el proveedor" });
    if (orden.renglones.some((r) => !(Number(r.precio) >= 0) || r.precio === "")) return Swal.fire({ icon: "warning", title: "Captura el precio de cada insumo" });
    setGuardando(true);
    try {
      const o = await api.post("ordenesGastosFlujo", {
        requisicionesGastos: orden.requisicion._id,
        proveedoresGastos: orden.proveedor,
        impuestos: Number(orden.impuestos),
        fecha: orden.fecha,
        observaciones: orden.observaciones,
        areas: process.env.REACT_APP_AREAS_GENERAL,
        renglones: orden.renglones.map((r) => ({ insumos: r.insumos[0]._id, cantidad: Number(r.cantidad), precio: Number(r.precio) })),
      });
      setOrden(null);
      await cargar();
      Swal.fire({ icon: "success", title: `Orden de gasto ${o.idOrdenGastos} creada`, text: "Queda por autorizar en Órdenes de gasto." });
    } catch (e) {
      Swal.fire({ icon: "error", title: "No se creó la orden", text: mensajeError(e) });
    }
    setGuardando(false);
  }

  if (!user.menu_gastos) return <Header />;

  return (
    <>
      <Header />
      <div className="card col-12" style={{ padding: "18px 22px" }}>
        <Row className="align-items-center">
          <Col md={7}>
            <h3 style={{ margin: 0 }}>Requisiciones de gastos</h3>
            <small style={{ color: "#64748b" }}>Paso 1 de 3: Requisición → Orden de gasto → Gasto</small>
          </Col>
          <Col md={5} style={{ textAlign: "right" }}>
            {user.gastos_create ? (
              <Button size="sm" color="success" onClick={abrirNueva}><i className="fas fa-plus"></i> Nueva requisición</Button>
            ) : null}{" "}
            <Button size="sm" color="danger" href="/MenuGastos">Regresar</Button>
          </Col>
        </Row>
        <Row style={{ margin: "14px 0 8px" }}>
          <Col md={2}><Label>Desde</Label><Input bsSize="sm" type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} /></Col>
          <Col md={2}><Label>Hasta</Label><Input bsSize="sm" type="date" value={fin} onChange={(e) => setFin(e.target.value)} /></Col>
          <Col md={2}>
            <Label>Estado</Label>
            <Input bsSize="sm" type="select" value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)}>
              <option value="">Todos</option>
              {["Por autorizar", "Autorizada", "Rechazada", "Ordenada", "Cancelada"].map((s) => <option key={s}>{s}</option>)}
            </Input>
          </Col>
        </Row>
        <Table size="sm" striped borderless className="table-responsive-xl">
          <thead>
            <tr><th>#</th><th>Fecha</th><th>Solicita</th><th>Categoría</th><th style={{ textAlign: "right" }}>Piezas</th><th>Estado</th><th>Autorizó</th><th>Observaciones</th><th></th></tr>
          </thead>
          <tbody>
            {lista.map((r) => (
              <tr key={r._id}>
                <td>{r.idRequisicionGastos}</td>
                <td>{r.fecha}</td>
                <td>{primero(r.solicitantes)}</td>
                <td>{[primero(r.categorias), primero(r.subCategorias)].filter(Boolean).join(" › ")}</td>
                <td style={{ textAlign: "right" }}>{numero(r.total_cantidad)}</td>
                <td><Estado valor={r.status} />{r.status === "Rechazada" && r.motivoRechazo ? <div style={{ fontSize: "0.7rem", color: "#991b1b" }}>{r.motivoRechazo}</div> : null}</td>
                <td>{nombreUsuario(r.autorizo)}</td>
                <td>{r.observaciones}</td>
                <td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                  <Button size="sm" color="info" title="Ver" onClick={() => verDetalle(r)}><i className="fas fa-eye"></i></Button>{" "}
                  <Button size="sm" color="danger" title="PDF" onClick={() => verDetalle(r, "pdf")}><i className="far fa-file-pdf"></i></Button>{" "}
                  {r.status === "Por autorizar" && user.gastos_autorizar ? (
                    <>
                      <Button size="sm" color="success" title="Autorizar" onClick={() => autorizar(r, "Autorizada")}><i className="fas fa-check"></i></Button>{" "}
                      <Button size="sm" color="warning" title="Rechazar" onClick={() => autorizar(r, "Rechazada")}><i className="fas fa-times"></i></Button>{" "}
                    </>
                  ) : null}
                  {r.status === "Autorizada" && user.gastos_create ? (
                    <><Button size="sm" color="primary" onClick={() => verDetalle(r, "orden")}><i className="fas fa-file-invoice-dollar"></i> Hacer orden</Button>{" "}</>
                  ) : null}
                  {["Por autorizar", "Autorizada", "Rechazada"].includes(r.status) && user.gastos_create ? (
                    <Button size="sm" color="secondary" title="Cancelar" onClick={() => cancelar(r)}><i className="fas fa-ban"></i></Button>
                  ) : null}
                </td>
              </tr>
            ))}
            {!lista.length ? <tr><td colSpan={9} style={{ color: "#94a3b8", textAlign: "center" }}>Sin requisiciones en el periodo.</td></tr> : null}
          </tbody>
        </Table>
      </div>

      {/* Nueva requisición */}
      <Modal size="lg" isOpen={!!nueva} toggle={() => setNueva(null)}>
        <ModalHeader toggle={() => setNueva(null)}>Nueva requisición de gasto</ModalHeader>
        <ModalBody>
          {nueva ? (
            <>
              <Row>
                <Col md={3}><Label>Fecha</Label><Input type="date" value={nueva.fecha} onChange={(e) => setNueva({ ...nueva, fecha: e.target.value })} /></Col>
                <Col md={4}>
                  <Label>Solicita *</Label>
                  <Input type="select" value={nueva.solicitantes} onChange={(e) => setNueva({ ...nueva, solicitantes: e.target.value })}>
                    <option value="">Selecciona</option>
                    {cat.solicitantes.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
                  </Input>
                </Col>
                <Col md={5}>
                  <Label>Categoría › Subcategoría</Label>
                  <Input type="select" value={nueva.subCategorias} onChange={(e) => setNueva({ ...nueva, subCategorias: e.target.value })}>
                    <option value="">Selecciona</option>
                    {cat.subCategorias
                      .slice()
                      .sort((a, b) => `${a.categorias[0].name}${a.name}`.localeCompare(`${b.categorias[0].name}${b.name}`))
                      .map((s) => <option key={s._id} value={s._id}>{`${s.categorias[0].name.trim()} › ${s.name.trim()}`}</option>)}
                  </Input>
                </Col>
              </Row>
              {cat.empresas.length > 1 ? (
                <Row style={{ marginTop: 8 }}>
                  <Col md={4}>
                    <Label>Empresa</Label>
                    <Input type="select" value={nueva.empresas} onChange={(e) => setNueva({ ...nueva, empresas: e.target.value })}>
                      <option value="">Selecciona</option>
                      {cat.empresas.map((e) => <option key={e._id} value={e._id}>{e.name}</option>)}
                    </Input>
                  </Col>
                </Row>
              ) : null}
              <Label style={{ marginTop: 8 }}>Observaciones</Label>
              <Input value={nueva.observaciones} onChange={(e) => setNueva({ ...nueva, observaciones: e.target.value })} />
              <h6 style={{ marginTop: 16 }}>Insumos</h6>
              {!cat.insumos.length ? (
                <p style={{ color: "#b45309" }}>No hay insumos dados de alta. Créalos primero en el catálogo de Insumos.</p>
              ) : null}
              {nueva.renglones.map((r) => {
                const ins = cat.insumos.find((i) => i._id === r.insumos);
                return (
                  <Row key={r.id} style={{ marginBottom: 6 }} className="align-items-center">
                    <Col md={5}>
                      <Input bsSize="sm" type="select" value={r.insumos} onChange={(e) => setRenglon(r.id, "insumos", e.target.value)}>
                        <option value="">Insumo...</option>
                        {cat.insumos
                          .filter((i) => i._id === r.insumos || !nueva.renglones.some((x) => x.insumos === i._id))
                          .map((i) => <option key={i._id} value={i._id}>{i.nombre} ({i.unidad})</option>)}
                      </Input>
                    </Col>
                    <Col md={2}>
                      <Input bsSize="sm" type="number" min="0" step="any" placeholder="Cantidad" value={r.cantidad} onChange={(e) => setRenglon(r.id, "cantidad", e.target.value)} />
                    </Col>
                    <Col md={3}>
                      <Input bsSize="sm" placeholder="Nota" value={r.observaciones} onChange={(e) => setRenglon(r.id, "observaciones", e.target.value)} />
                    </Col>
                    <Col md={2} style={{ fontSize: "0.72rem", color: "#64748b" }}>
                      {ins ? `Hay ${numero(ins.existencia)}` : ""}{" "}
                      <Button size="sm" color="danger" disabled={nueva.renglones.length === 1} onClick={() => setNueva({ ...nueva, renglones: nueva.renglones.filter((x) => x.id !== r.id) })}>−</Button>
                    </Col>
                  </Row>
                );
              })}
              <Button size="sm" color="info" onClick={() => setNueva({ ...nueva, renglones: [...nueva.renglones, nuevoRenglon()] })}>+ Agregar insumo</Button>
            </>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="success" onClick={guardarNueva} disabled={guardando}>{guardando ? "Guardando..." : "Guardar requisición"}</Button>
          <Button color="secondary" onClick={() => setNueva(null)}>Cerrar</Button>
        </ModalFooter>
      </Modal>

      {/* Detalle */}
      <Modal size="lg" isOpen={!!detalle} toggle={() => setDetalle(null)}>
        <ModalHeader toggle={() => setDetalle(null)}>
          Requisición {detalle ? detalle.requisicion.idRequisicionGastos : ""} {detalle ? <Estado valor={detalle.requisicion.status} /> : null}
        </ModalHeader>
        <ModalBody>
          {detalle ? (
            <>
              <p style={{ fontSize: "0.85rem" }}>
                {detalle.requisicion.fecha} · Solicita <b>{primero(detalle.requisicion.solicitantes)}</b> · Capturó {nombreUsuario(detalle.requisicion.creo)}
                {detalle.requisicion.autorizo && detalle.requisicion.autorizo[0] ? ` · ${detalle.requisicion.status === "Rechazada" ? "Rechazó" : "Autorizó"} ${nombreUsuario(detalle.requisicion.autorizo)} (${detalle.requisicion.fechaAutorizacion})` : ""}
              </p>
              <Table size="sm" bordered>
                <thead><tr><th>Insumo</th><th>Unidad</th><th style={{ textAlign: "right" }}>Cantidad</th><th style={{ textAlign: "right" }}>Existencia hoy</th><th>Nota</th></tr></thead>
                <tbody>
                  {detalle.renglones.map((r) => (
                    <tr key={r._id}>
                      <td>{primero(r.insumos, "nombre")}</td>
                      <td>{primero(r.insumos, "unidad")}</td>
                      <td style={{ textAlign: "right" }}>{numero(r.cantidad)}</td>
                      <td style={{ textAlign: "right" }}>{numero(r.insumos && r.insumos[0] ? r.insumos[0].existencia : 0)}</td>
                      <td>{r.observaciones}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          ) : null}
        </ModalBody>
        <ModalFooter><Button color="secondary" onClick={() => setDetalle(null)}>Cerrar</Button></ModalFooter>
      </Modal>

      {/* Hacer orden */}
      <Modal size="lg" isOpen={!!orden} toggle={() => setOrden(null)}>
        <ModalHeader toggle={() => setOrden(null)}>Orden de gasto — requisición {orden ? orden.requisicion.idRequisicionGastos : ""}</ModalHeader>
        <ModalBody>
          {orden ? (
            <>
              <Row>
                <Col md={5}>
                  <Label>Proveedor *</Label>
                  <Input type="select" value={orden.proveedor} onChange={(e) => setOrden({ ...orden, proveedor: e.target.value })}>
                    <option value="">Selecciona</option>
                    {cat.proveedores.map((p) => <option key={p._id} value={p._id}>{p.nombre}</option>)}
                  </Input>
                  {!cat.proveedores.length ? <small style={{ color: "#b45309" }}>Da de alta proveedores en "Proveedores de gastos".</small> : null}
                </Col>
                <Col md={3}><Label>Fecha</Label><Input type="date" value={orden.fecha} onChange={(e) => setOrden({ ...orden, fecha: e.target.value })} /></Col>
                <Col md={2}>
                  <Label>IVA</Label>
                  <Input type="select" value={orden.impuestos} onChange={(e) => setOrden({ ...orden, impuestos: e.target.value })}>
                    <option value={0}>No</option>
                    <option value={16}>16%</option>
                  </Input>
                </Col>
              </Row>
              <Table size="sm" bordered style={{ marginTop: 12 }}>
                <thead><tr><th>Insumo</th><th style={{ textAlign: "right" }}>Cantidad</th><th style={{ width: 140 }}>Precio unitario</th><th style={{ textAlign: "right" }}>Importe</th></tr></thead>
                <tbody>
                  {orden.renglones.map((r) => (
                    <tr key={r._id}>
                      <td>{primero(r.insumos, "nombre")} <small style={{ color: "#64748b" }}>({primero(r.insumos, "unidad")})</small></td>
                      <td style={{ textAlign: "right" }}>{numero(r.cantidad)}</td>
                      <td>
                        <Input bsSize="sm" type="number" min="0" step="0.01" value={r.precio}
                          onChange={(e) => { const v = e.target.value; setOrden((o) => ({ ...o, renglones: o.renglones.map((x) => (x._id === r._id ? { ...x, precio: v } : x)) })); }} />
                      </td>
                      <td style={{ textAlign: "right" }}>{dinero(Number(r.cantidad) * (Number(r.precio) || 0))}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr><td colSpan={3} style={{ textAlign: "right" }}>Subtotal</td><td style={{ textAlign: "right" }}>{dinero(totalesOrden.subtotal)}</td></tr>
                  <tr><td colSpan={3} style={{ textAlign: "right" }}>IVA</td><td style={{ textAlign: "right" }}>{dinero(totalesOrden.iva)}</td></tr>
                  <tr><td colSpan={3} style={{ textAlign: "right", fontWeight: 700 }}>Total</td><td style={{ textAlign: "right", fontWeight: 700 }}>{dinero(totalesOrden.total)}</td></tr>
                </tfoot>
              </Table>
              <Label>Observaciones</Label>
              <Input value={orden.observaciones} onChange={(e) => setOrden({ ...orden, observaciones: e.target.value })} />
            </>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="success" onClick={guardarOrden} disabled={guardando}>{guardando ? "Creando..." : "Crear orden de gasto"}</Button>
          <Button color="secondary" onClick={() => setOrden(null)}>Cerrar</Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default RequisicionesGastos;
